'use client';

import {type PropsWithChildren, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';

import {Skeleton} from '../skeleton';

export const TableOfContents = ({children}: PropsWithChildren) => {
	const [loadingHeadings, setLoadingHeadings] = useState(true);
	const [headings, setHeadings] = useState<
		{title: string; href: string; id: string}[]
	>([]);
	const [currentlyInView, setCurrentlyInView] = useState<string | null>(null);

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const h2Elements = ref.current?.querySelectorAll('h2[id]');

		if (!h2Elements) {
			return;
		}

		const newHeadings = Array.from(h2Elements).map((h2) => ({
			title: h2.textContent ?? '',
			href: `#${h2.id}`,
			id: h2.id,
		}));

		const intersectionObserver = new IntersectionObserver(
			(enties) => {
				enties.forEach((entry) => {
					if (entry.isIntersecting) {
						setCurrentlyInView(entry.target.id);
					}
				});
			},
			{rootMargin: '-10% 0px -70% 0px'},
		);

		h2Elements.forEach((h2) => {
			intersectionObserver.observe(h2);
		});

		setHeadings(newHeadings);
		setLoadingHeadings(false);

		return () => {
			intersectionObserver.disconnect();
		};
	}, [setHeadings]);

	return (
		<Container as="section">
			<div className="@container">
				<div
					className={clsx(
						'grid',
						'@2xl:grid-cols-12',
						'@2xl:gap-10',
						'relative',
					)}
				>
					<div
						className={clsx(
							'@2xl:col-start-9',
							'@2xl:-col-end-1',
							'@2xl:row-start-1',
							'relative',
						)}
					>
						<div className={clsx('sticky', 'top-10', 'mt-14')}>
							<Heading
								level={2}
								id="table-of-contents"
								size="md"
								spacing
							>
								Innhold
							</Heading>
							{loadingHeadings ? (
								<div
									className={clsx(
										'border-l',
										'border-gray-300',
										'pl-4',
										'py-1',
									)}
								>
									<Skeleton className="my-3 h-[16px]" />
									<Skeleton className="my-3 h-[16px]" />
									<Skeleton className="my-3 h-[16px]" />
									<Skeleton className="my-3 h-[16px]" />
									<Skeleton className="my-3 h-[16px]" />
								</div>
							) : (
								<nav aria-label="Innholdsfortegnelse">
									<ol
										aria-labelledby="table-of-contents"
										className={clsx('pl-4')}
									>
										{headings.map((heading) => (
											<li
												key={heading.href}
												className={clsx('text-grey-15')}
											>
												<Link
													href={heading.href}
													className={clsx(
														'block',
														'py-1.5',
														'hover:text-primary-2',
														'relative',
														'before:absolute',
														heading.id ===
															currentlyInView
															? [
																	'before:bg-primary-25',
																	'text-primary-2',
																]
															: [
																	'before:bg-grey-border',
																	'text-grey-15',
																],
														'before:w-[2px]',
														'before:-left-4',
														'before:top-0',
														'before:h-full',
														'hover:before:bg-primary-25',
														'transition-all',
														'peer',
													)}
												>
													{heading.title}
												</Link>
											</li>
										))}
									</ol>
								</nav>
							)}
						</div>
					</div>
					<div
						ref={ref}
						className={clsx(
							'@2xl:col-start-1 @2xl:col-end-8',
							'@2xl:row-start-1',
						)}
					>
						{children}
					</div>
				</div>
			</div>
		</Container>
	);
};
